import {useRef, useState, useCallback, RefObject} from 'react';

interface VideoPlayerState {
    videoRef: RefObject<HTMLVideoElement | null>;
    isPlaying: boolean;
    error: string | null;
    handlePlay: () => Promise<void>;
}

export const useVideoPlayer = (requestFullscreenOnMobile: boolean = false): VideoPlayerState => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handlePlay = useCallback(async () => {
        const videoElement = videoRef.current;
        if (!videoElement) {
            setError("Video element is not available.");
            return;
        }

        setError(null); // Clear previous errors
        setIsPlaying(true);

        try {
            await videoElement.play();

            const isMobile = /Mobi/i.test(window.navigator.userAgent);
            if (requestFullscreenOnMobile && isMobile) {
                if (videoElement.requestFullscreen) {
                    await videoElement.requestFullscreen();
                } else if ((videoElement as any).webkitEnterFullscreen) { // Safari
                    (videoElement as any).webkitEnterFullscreen();
                }
            }
        } catch (err) {
            setIsPlaying(false);
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
            setError(`Failed to play video: ${errorMessage}`);
            console.error("Error playing or entering fullscreen:", err);
        }
    }, [requestFullscreenOnMobile]);

    // We can also expose a function to update isPlaying from outside, e.g., onEnded event
    // For now, the component can handle this directly.

    return {videoRef, isPlaying, error, handlePlay};
};